import React from 'react';
import { Document, StyleSheet, Page, View, Text, Font, Image } from '@react-pdf/renderer';
import montserratLight from '../assets/fonts/Montserrat-Light.ttf'
import montserratBold from '../assets/fonts/Montserrat-Bold.ttf'
import Logo from '../assets/icons/R_logo.png';
import FooterLogo from '../assets/icons/R_footer.png';
import WelcomeArrow from '../assets/icons/welcome.png';
import isEmpty from 'lodash.isempty';
import { PRINT_COLORS } from '../lib/printerHelper';
// PDFs are rendered by @react-pdf/renderer OUTSIDE the React tree (pdf().toBlob()), so there
// is no I18nContext above them - they use the standalone t() and the active locale instead.
import { t } from '../i18n';
import { formatWeekday } from '../i18n/dates';

Font.register({ family: 'Montserrat-Light', src: montserratLight });
Font.register({ family: 'Montserrat-Bold', src: montserratBold });

const { BLACK, BLUE, DARK_BLUE } = PRINT_COLORS;

const createStyles = (isMono) => StyleSheet.create({
  page: { padding: '20px 40px 0 40px', fontFamily: 'Montserrat-Light', fontSize: 10, color: isMono ? BLACK : DARK_BLUE },
  title: {
    fontFamily: 'Montserrat-Bold',
    fontSize: 14,
    textTransform: 'uppercase',
    borderBottomWidth: 1,
    borderColor: isMono ? BLACK : BLUE,
    width: '100%',
    color: isMono ? BLACK : BLUE,
    marginTop: 10,
    marginBottom: 6,
    paddingBottom: 2
  },
  bold: { fontFamily: 'Montserrat-Bold', fontSize: 10 },
  phoneNumber: {
    borderBottomWidth: 1,
    borderColor: 'black',
    borderStyle: 'dotted',
    height: '8.89mm',
    marginBottom: '3.3mm',
    marginTop: 1,
    fontSize: 16,
    position: 'relative'
  },
  phoneNumberText: { position: 'absolute', bottom: 0, left: 0, opacity: 1 },
  residentContainer: { height: '37.08mm' },
  medicalContainer: { height: '61.72mm' },
  telephoneContainer: { height: '51.05mm' },
  meetContainer: { height: '98mm' },
  bottomLogoContainer: { height: '11.94mm', marginTop: 20 },
  bottomLogo: { marginLeft: 'auto', marginRight: -12, width: 30, height: 'auto' },
  header: { height: '18mm', flexDirection: 'row', justifyContent: 'space-between' },
  headerBackgroundArrow: { width: 30, height: 'auto', position: 'absolute', bottom: -20, left: '30%', zIndex: -1 },
  headerBackgroundText: { color: 'white', fontFamily: 'Montserrat-Bold', fontSize: 20, textAlign: 'center' },
  headerImage: { width: 'auto', height: 50, padding: '6px 0' },
  headerBackground: { backgroundColor: isMono ? '#fff' : '#d8006b', padding: '5px 10px', borderRadius: 10, width: 230, height: 37 },
  dataContainer: { marginTop: 15 },
  resident: { opacity: 1, marginTop: 1, fontSize: 13 },
  hotel: { opacity: 1, marginTop: 5, fontSize: 11 },
  welcomeText: { marginTop: 3, opacity: 1 }
});

const createTableStyles = (isMono) => StyleSheet.create({
  mainContainer: { opacity: 1, width: '100%', borderWidth: 1, borderColor: isMono ? BLACK : BLUE, borderRadius: 10, marginTop: 7, fontFamily: 'Montserrat-Bold' },
  row: { width: '100%', flexDirection: 'row', height: '12mm' },
  cell: { padding: '15px 6px 5px 6px', position: 'relative', fontSize: 10 },
  date: { width: '16%', borderColor: isMono ? BLACK : BLUE, borderRightWidth: 1, textTransform: 'capitalize' },
  time: { width: '10%', borderColor: isMono ? BLACK : BLUE, borderRightWidth: 1 },
  place: { width: '45%' },
  visitPlace: { width: '35%' },
  resident: { width: '37%', borderColor: isMono ? BLACK : BLUE, borderTopWidth: 1, borderRightWidth: 1 },
  visitResident: { width: '31%', borderColor: isMono ? BLACK : BLUE, borderTopWidth: 1, borderRightWidth: 1 },
  phoneNumber: { width: '15%', borderColor: isMono ? BLACK : BLUE, borderTopWidth: 1 },
  visitPhoneNumber: { width: '18%', borderColor: isMono ? BLACK : BLUE, borderTopWidth: 1 },
  info: { fontSize: 8, position: 'absolute', top: 3, left: 5, fontFamily: 'Montserrat-Light' },
  type: { width: '29%', borderColor: isMono ? BLACK : BLUE, borderRightWidth: 1 }
})

export default function MyDocument(props) {
  const { emergencyNumber, isScaffold, residentName, data, hotel, welcomeText, theme } = props;
  const isMonoColor = theme !== 'color';
  const tableStyles = createTableStyles(isMonoColor);
  const styles = createStyles(isMonoColor);

  const generateMeetingTable = tableData => {
    return (
      <View style={tableStyles.mainContainer}>
        <View style={tableStyles.row}>
          <View style={[tableStyles.cell, tableStyles.type]}>
            <Text>{t('pdf.welcome.infoMeeting')}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.date]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.date')}</Text>
            <Text>{formatWeekday(tableData.date)}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.time]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.time')}</Text>
            <Text>{tableData.time}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.place]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.place')}</Text>
            <Text>{tableData.place}</Text>
          </View>
        </View>
      </View>
    );
  }

  const generateVisitTable = tableData => {
    return (
      <View style={tableStyles.mainContainer}>
        <View style={tableStyles.row}>
          <View style={[tableStyles.cell, tableStyles.type]}>
            <Text>{t('pdf.welcome.hotelVisit')}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.date]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.date')}</Text>
            <Text>{formatWeekday(tableData.date)}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.time]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.from')}</Text>
            <Text>{tableData.timeFrom}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.time]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.to')}</Text>
            <Text>{tableData.timeTo}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.visitPlace]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.place')}</Text>
            <Text>{tableData.place}</Text>
          </View>
        </View>
      </View>
    );
  }

  const generatePhoneTable = tableData => {
    return (
      <View style={tableStyles.mainContainer}>
        <View style={tableStyles.row}>
          <View style={[tableStyles.cell, tableStyles.type]}>
            <Text>{t('pdf.welcome.phoneDuty')}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.date]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.date')}</Text>
            <Text>{formatWeekday(tableData.date)}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.time]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.from')}</Text>
            <Text>{tableData.timeFrom}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.time]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.to')}</Text>
            <Text>{tableData.timeTo}</Text>
          </View>
          <View style={[tableStyles.cell, tableStyles.visitPlace]}>
            <Text style={tableStyles.info}>{t('pdf.welcome.place')}</Text>
            <Text>{t('pdf.welcome.phoneContact')}</Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <Document>
      {data.map((page, pageIndex) => (
        <Page size="A4" key={pageIndex} style={styles.page}>
          <View style={{ ...styles.header, opacity: isScaffold ? 0 : 1 }}>
            <Image src={Logo} style={styles.headerImage} />
            <View style={styles.headerBackground}>
              <Text style={styles.headerBackgroundText}>{t('pdf.welcome.headerBadge')}</Text>
              <Image src={WelcomeArrow} style={styles.headerBackgroundArrow} />
            </View>
          </View>
          <View style={{ ...styles.residentContainer, opacity: isScaffold ? 0 : 1 }}>
            <Text style={styles.title}>{t('pdf.welcome.resident')}</Text>
            <Text style={[styles.bold, styles.resident]}>{residentName}</Text>
            <Text style={[styles.bold, styles.hotel]}>{hotel}</Text>
            <Text style={styles.welcomeText}>{welcomeText}</Text>
          </View>
          <View style={{ ...styles.medicalContainer, opacity: isScaffold ? 0 : 1 }}>
            <Text style={styles.title}>{t('pdf.welcome.medicalAssistance')}</Text>
            <Text>{t('pdf.welcome.insurance')}</Text>
            <Text>{t('pdf.welcome.policyIntro')}<Text style={styles.bold}>RBT/2/Z/08</Text>.</Text>
            <Text>{'\n'}</Text>
            <Text>{t('pdf.welcome.lifeThreatening')}<Text style={styles.bold}>{t('pdf.welcome.lifeThreateningBold')}</Text></Text>
            <Text>{'\n'}</Text>
            <Text>{t('pdf.welcome.nonLifeThreatening')}<Text style={styles.bold}>{t('pdf.welcome.helpCentreBold')}</Text></Text>
            <Text>{t('pdf.welcome.polishSpeaking')}</Text>
            <Text>{'\n'}</Text>
            <Text><Text style={styles.bold}>{t('common.warning')}</Text>{t('pdf.welcome.noticeText')}</Text>
          </View>
          <View style={{ ...styles.telephoneContainer, opacity: isScaffold ? 0 : 1 }}>
            <Text style={styles.title}>{t('pdf.welcome.emergencyPhone')}</Text>
            <Text>{t('pdf.welcome.emergencyText1')}</Text>
            <View style={styles.phoneNumber}>
              <Text style={[styles.phoneNumberText, styles.bold]}>{emergencyNumber}</Text>
            </View>
            <Text>{t('pdf.welcome.emergencyText2')}</Text>
          </View>
          <View style={{ ...styles.meetContainer, opacity: isScaffold ? 0 : 1 }}>
            <Text style={styles.title}>{t('pdf.welcome.meetings')}</Text>
            <Text>{t('pdf.welcome.meetingsText')}</Text>
            <View style={styles.dataContainer}>
              {!isEmpty(page.comment) && <Text style={{ opacity: 1 }}>{page.comment}</Text>}
              {page.tables.map(table => {
                if (table.type === 'meeting') return generateMeetingTable(table.data);
                if (table.type === 'visit') return generateVisitTable(table.data);
                if (table.type === 'phone') return generatePhoneTable(table.data);
                return null;
              })}
            </View>
          </View>
          <View style={{ ...styles.bottomLogoContainer, opacity: isScaffold ? 0 : 1 }}>
            <Image src={FooterLogo} style={styles.bottomLogo} />
          </View>
        </Page>
      ))}
    </Document>
  );
}
